import { Text } from '@loomhq/lens';
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';

import { LOOM_PRO } from 'constants/capabilities';
import {
  ADD_CUSTOM_VIEWERS_CTA_CLICKED,
  SHARE_VIDEO_PRIVACY_MENU_TOGGLED,
} from 'constants/events';
import { updateVideoSettings } from 'creators/shareVideo';
import * as analytics from 'utilities/analytics';

import PrivacySection from './privacy-section';
import {
  HELP_ADD_PASSWORD,
  HELP_CUSTOM_ACCESS,
  HELP_SEARCH_INDEXING,
} from '../../../constants/feature-tour';

import CopyLinkSection from 'components/share-page-common/copy-link';
import CustomAccessSettings from 'components/share-video/custom-access-editor';
import PrivacySettings from 'components/share-video/privacy-settings';
import Tooltip from 'components/ui-components/tooltip';

import './styles.less';

class ShareVideoWidget extends PureComponent {
  static defaultProps = {
    video: {},
  };

  state = {
    passwordFlow: false,
    customViewerFlow: false,
    privacyOptionsVisible: true,
  };

  onVideoPrivacyChange = publicStatus =>
    this.props.updateVideoSettings({
      allow_search_engine_indexing: publicStatus,
    });

  onCustomViewerClick = () => {
    analytics.track(ADD_CUSTOM_VIEWERS_CTA_CLICKED);

    this.setState({ customViewerFlow: true });
  };

  exitEditMode = () =>
    this.setState({
      passwordFlow: false,
      customViewerFlow: false,
    });

  onAddPasswordClick = () => this.setState({ passwordFlow: true });

  togglePrivacyOptions = option => {
    const res =
      option !== undefined ? option : !this.state.privacyOptionsVisible;

    this.setState({
      privacyOptionsVisible: res,
    });

    analytics.track(SHARE_VIDEO_PRIVACY_MENU_TOGGLED, {
      visible: res,
    });
  };

  render() {
    const {
      video,
      hasPro,
      customAccessTourIsActive,
      addPasswordTourIsActive,
      searchIndexingTourIsActive,
    } = this.props;
    const {
      passwordFlow,
      customViewerFlow,
      privacyOptionsVisible,
    } = this.state;
    const inEditMode = passwordFlow || customViewerFlow;

    return (
      <div
        className="share-video-methods"
        // we have to use inline styles here instead of classes because
        // the sidebar animation from animejs overrides any class defined
        // height. That leads to overflow when this component is expanded
        // https://github.com/juliangarnier/anime/issues/77
        style={{ height: 'auto' }}
      >
        {!inEditMode && (
          <>
            <div className="section-header">
              <Text variant="title">Send Your Video</Text>
              <div className="share-video-header-status">
                {video.is_protected && (
                  <div className="share-video-header-locked">
                    <Tooltip>Video has password</Tooltip>
                  </div>
                )}
                {video.permitted_viewers_only && (
                  <div className="share-video-header-custom-viewer">
                    <Tooltip>Video only visible to permitted viewers</Tooltip>
                  </div>
                )}
              </div>
            </div>
            <CopyLinkSection />
            <div className="section-divider" />
            <PrivacySection
              showUpgradeModal={this.props.showUpgradeModal}
              customAccessTourIsActive={customAccessTourIsActive}
              addPasswordTourIsActive={addPasswordTourIsActive}
              searchIndexingTourIsActive={searchIndexingTourIsActive}
              expanded={privacyOptionsVisible}
              toggleExpansion={this.togglePrivacyOptions}
              video={video}
              hasPro={hasPro}
              onCustomViewerClick={this.onCustomViewerClick}
              onAddPasswordClick={this.onAddPasswordClick}
              onVideoPrivacyChange={this.onVideoPrivacyChange}
            />
          </>
        )}
        {customViewerFlow && (
          <CustomAccessSettings
            video={video}
            onSaveClick={this.exitEditMode}
            onExitClick={this.exitEditMode}
          />
        )}
        {passwordFlow && (
          <PrivacySettings
            video={video}
            onSaveClick={this.exitEditMode}
            onCancelClick={this.exitEditMode}
          />
        )}
      </div>
    );
  }
}

const mapStateToProps = state => {
  const { video } = state.share_video;
  const { current: user } = state.user;
  const { currentTour, tourOpenAndActive } = state.featureTour;

  const customAccessTourIsActive =
    currentTour === HELP_CUSTOM_ACCESS && tourOpenAndActive;
  const addPasswordTourIsActive =
    currentTour === HELP_ADD_PASSWORD && tourOpenAndActive;
  const searchIndexingTourIsActive =
    currentTour === HELP_SEARCH_INDEXING && tourOpenAndActive;

  return {
    customAccessTourIsActive,
    addPasswordTourIsActive,
    searchIndexingTourIsActive,
    video,
    hasPro: user && user.capabilities[LOOM_PRO],
  };
};

const mapDispatchToProps = dispatch => ({
  updateVideoSettings: settings =>
    dispatch(updateVideoSettings(settings, null)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ShareVideoWidget);
